import { handleDatesRequest, handleDatesResponse } from "@/helpers/handle-date.ts";
import { toasts } from "@skailer-com/uikit";
import i18n from "@/i18n.ts";
import { queryClient } from "@/queryClient.ts";
import { useAuthStore } from "@/stores/employee.store.ts";
import { ApiErrors, ApiResponse, ApiValidationError } from "@/types/api.types.ts";
import * as Sentry from "@sentry/react";
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  GenericAbortSignal,
  InternalAxiosRequestConfig,
} from "axios";

export const IMPERSONATION_TOKEN_KEY = "impersonation_token";

export function getImpersonationToken(): string | null {
  try {
    return sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);
  } catch {
    return null;
  }
}

// Флаг в конфиге, а не sessionStorage: первый 401 чистит его, и параллельные 401 ушли бы на /login.
type ImpersonationConfig = { isImpersonation?: boolean };

declare module "axios" {
  interface AxiosRequestConfig {
    keepDateStrings?: boolean;
    skipNotFoundToast?: boolean;
  }
}

const MAX_CONCURRENT_REQUESTS = 10;

class RequestSemaphore {
  private running = 0;
  private readonly queue: Array<() => void> = [];

  constructor(private readonly limit: number) {}

  acquire(): Promise<void> {
    if (this.running < this.limit) {
      this.running++;
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.queue.push(() => {
        this.running++;
        resolve();
      });
    });
  }

  release(): void {
    this.running--;
    const next = this.queue.shift();
    if (next) next();
  }
}

const semaphore = new RequestSemaphore(MAX_CONCURRENT_REQUESTS);

const REQUEST_TIMEOUT_MS = 30_000;

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_BASE_API,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    Accept: "application/json",
    responseType: "json",
  },
});

function sendErrorToSentry(
  error: AxiosError,
  extra?: { errorText?: string; fullResponse?: unknown; validationErrors?: string }
): void {
  const apiUrl = (error.config?.baseURL?.replace(/\/$/, "") ?? "") + (error.config?.url ?? "");
  const userId = useAuthStore.getState().userId;
  const page = window.location.href;
  const status = error.response?.status;

  Sentry.withScope((scope) => {
    scope.setTags({
      api_url: apiUrl || "unknown",
      status: status?.toString() || "unknown",
    });

    if (userId) {
      scope.setUser({ id: userId.toString() });
    }

    scope.setExtras({
      userId,
      page,
      status,
      ...extra,
    });

    Sentry.captureException(error);
  });
}

function handleSuccessResponse(response: AxiosResponse) {
  if (!response.config.keepDateStrings) handleDatesResponse(response.data);

  const redirectTo = response.data?.meta?.redirect_to;
  if (redirectTo) {
    window.location.href = redirectTo;
    return;
  }

  return {
    data: response.data,
    success: true,
    headers: response.headers,
  } as unknown as AxiosResponse;
}

function handleErrorResponse(error: unknown) {
  if (axios.isAxiosError(error)) {
    if (error.code === "ECONNABORTED" || error.code === "ERR_CANCELED") {
      return { success: false, error };
    }

    if (error.response?.status === 401) {
      const isImpersonationRequest =
        (error.config as ImpersonationConfig | undefined)?.isImpersonation ||
        getImpersonationToken();
      if (isImpersonationRequest) {
        try {
          sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
        } catch {
          /* noop */
        }
        queryClient.clear();
        if (window.location.pathname !== "/impersonation-ended") {
          window.location.href = "/impersonation-ended";
        }
      } else if (
        window.location.pathname !== "/login" &&
        !window.location.pathname.startsWith("/invite") &&
        !window.location.pathname.startsWith("/reset-password")
      ) {
        queryClient.clear();
        window.location.href = "/login";
      } else {
        if (error.response?.data?.error?.code) {
          toasts.showError({ title: i18n.t(`api_errors:${error.response.data.error.code}`) });
        }
      }
    } else if (error.response?.status === 403) {
      const path = window.location.pathname;
      if (!path.startsWith("/invite")) {
        toasts.showError({ title: i18n.t("common:access_denied") });
      }
      const errorText = error.response?.data?.error?.message || error.response?.data?.error?.code;
      sendErrorToSentry(error, { errorText });
    } else if (error.response?.status === 404 && !error.config?.skipNotFoundToast) {
      const errorTitleCode = error.response?.data?.error?.code
        ? `api_errors:${error.response.data.error.code}`
        : "common:requested_document_not_found";
      toasts.showError({ title: i18n.t(errorTitleCode) });
    } else if (error.response?.status === 500) {
      sendErrorToSentry(error, { fullResponse: error.response?.data });
      toasts.showError({ title: i18n.t("errors:server_error") });
    } else if (error.response?.status === 422) {
      sendErrorToSentry(error, {
        fullResponse: error.response?.data,
        validationErrors: (error.response?.data.error.validation_errors || [])
          .map(
            ({ field, code, message }: { field: string; code: string; message: string }) =>
              `${field}: ${message} [${code}]`
          )
          .join("\n"),
      });
      return {
        success: false,
        error: {
          status: ApiErrors.validationError,
          validationErrors: error.response?.data.error.validation_errors || [],
        } as ApiValidationError,
      };
    }
  }

  return { success: false, error };
}

axiosInstance.interceptors.request.use(async (config) => {
  await semaphore.acquire();
  return config;
});

axiosInstance.interceptors.request.use((config) => {
  const impersonationToken = getImpersonationToken();
  const token = impersonationToken ?? useAuthStore.getState().token;
  const userId = useAuthStore.getState().userId;

  config.headers["X-Language"] = i18n.language;

  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
    if (impersonationToken) {
      (config as typeof config & ImpersonationConfig).isImpersonation = true;
    }
  }

  // X-User-Id под impersonation остаётся id админа: бэк опознаёт сессию только по токену.
  if (userId) {
    config.headers["X-User-Id"] = userId.toString();
  }

  return config;
});

axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { method } = config;
  if (method === "post" || method === "put") {
    handleDatesRequest(config.data);
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    semaphore.release();
    return handleSuccessResponse(response) as AxiosResponse;
  },
  (error) => {
    semaphore.release();
    return handleErrorResponse(error);
  }
);

const apiInstanceTyped = (instance: AxiosInstance) => ({
  get: <T>(
    url: string,
    params: object = {},
    signal: GenericAbortSignal | undefined = undefined,
    config: AxiosRequestConfig<T> | undefined = undefined
  ): Promise<ApiResponse<T>> =>
    instance.get<never, ApiResponse<T>>(url, { signal, params, ...config }),

  post: <T>(
    url: string,
    body: object,
    signal: GenericAbortSignal | undefined = undefined,
    config: AxiosRequestConfig<T> | undefined = undefined
  ): Promise<ApiResponse<T>> =>
    instance.post<never, ApiResponse<T>>(url, body, { signal, ...config }),

  put: <T>(url: string, body: object): Promise<ApiResponse<T>> =>
    instance.put<never, ApiResponse<T>>(url, body),

  delete: <T>(
    url: string,
    body?: object,
    signal: GenericAbortSignal | undefined = undefined,
    config: AxiosRequestConfig<T> | undefined = undefined
  ): Promise<ApiResponse<T>> =>
    instance.delete<never, ApiResponse<T>>(url, {
      signal,
      data: body, // В axios тело для DELETE передается через data
      ...config,
    }),

  patch: <T>(url: string, body: object): Promise<ApiResponse<T>> =>
    instance.patch<never, ApiResponse<T>>(url, body),
});

export const api = apiInstanceTyped(axiosInstance);
